import { useCallback, useEffect, useMemo, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import { useDebounce } from 'rooks';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Modal } from 'antd';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { isDevelopment } from '@constants/settings';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import {
    AIRFLOW_BASE_PATH,
    DAG_LIST_LIMIT,
    DAG_ORDER_BY,
    DAG_SORT_COLUMN_TO_API_FIELD,
    MODEL_REQUEST_DAG_PREFIX,
    PAUSED_FILTER,
    PAUSED_FILTER_VALUE,
    PausedFilter,
    SEARCH_DEBOUNCE_MS,
    dagDeletedToastMessage,
} from '../constants';
import { AirflowDag } from '../types';

export type PausedFilterCounts = { all: number; active: number; paused: number };

const EMPTY_FILTER_COUNTS: PausedFilterCounts = { all: 0, active: 0, paused: 0 };

/*
 * Manages the DAGs list: filters, sort, pagination, pause toggle, trigger dialog, delete,
 * and the All/Active/Paused tab counts
 */
export function useDagsTab() {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();

    /*
     * Airflow requests build their host from Env.REACT_APP_AIRFLOW_URL, which is only
     * populated once the app's post-login env-variables fetch resolves (see SideBars). On a
     * hard reload of this route Env starts out null, so fetching before it's ready would hit
     * a malformed "undefined/api/v1" URL instead of the real Airflow host.
     */
    const isAirflowConfigReady = isDevelopment || Boolean(Env?.REACT_APP_AIRFLOW_URL);

    const [dags, setDags] = useState<AirflowDag[]>([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pausedFilter, setPausedFilter] = useState<PausedFilter>(PAUSED_FILTER.all);
    const [filterCounts, setFilterCounts] = useState<PausedFilterCounts>(EMPTY_FILTER_COUNTS);
    const [tagFilter, setTagFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [togglingDagId, setTogglingDagId] = useState<string | null>(null);
    const [triggerDialogDagId, setTriggerDialogDagId] = useState<string | null>(null);

    const updateDebouncedSearch = useDebounce((value: string) => {
        setDebouncedSearch(value);
        setOffset(0);
    }, SEARCH_DEBOUNCE_MS);

    /*
     * Updates the search input immediately and schedules the debounced fetch trigger
     */
    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            updateDebouncedSearch(value);
        },
        [updateDebouncedSearch]
    );

    /*
     * Fetches the DAGs list against the current filters, sort, and pagination state
     */
    const fetchDags = useCallback(
        async (showLoader = true) => {
            if (!isAirflowConfigReady) return;
            if (showLoader) setIsLoading(true);
            try {
                const sort = sorting[0];
                const orderBy = sort ? `${sort.desc ? '-' : ''}${DAG_SORT_COLUMN_TO_API_FIELD[sort.id] ?? sort.id}` : DAG_ORDER_BY;
                const response = await AirflowDashboardHandler.listDags(Env, {
                    limit: DAG_LIST_LIMIT,
                    offset,
                    orderBy,
                    onlyActive: true,
                    dagIdPattern: debouncedSearch || undefined,
                    paused: PAUSED_FILTER_VALUE[pausedFilter],
                    tags: tagFilter ? [tagFilter] : undefined,
                });
                setDags(response.dags);
                setTotalEntries(response.total_entries);
            } catch {
                errorAlert('Failed to load DAGs.');
            } finally {
                setIsLoading(false);
                setIsRefreshing(false);
            }
        },
        [Env, offset, sorting, debouncedSearch, pausedFilter, tagFilter, isAirflowConfigReady]
    );

    useEffect(() => {
        fetchDags();
    }, [fetchDags]);

    useEffect(() => {
        if (!isAirflowConfigReady) return;
        (async () => {
            try {
                const response = await AirflowDashboardHandler.listDagTags(Env, { limit: 100, orderBy: 'name' });
                setAvailableTags(response.tags);
            } catch {
                setAvailableTags([]);
            }
        })();
    }, [isAirflowConfigReady]);

    /*
     * Fetches the All/Active/Paused tab counts via three real listDags calls (limit 1, just
     * reading total_entries) — independent of the current filter/page/search
     */
    const fetchFilterCounts = useCallback(async () => {
        if (!isAirflowConfigReady) return;
        try {
            const [all, active, paused] = await Promise.all([
                AirflowDashboardHandler.listDags(Env, { limit: 1, onlyActive: true }),
                AirflowDashboardHandler.listDags(Env, { limit: 1, onlyActive: true, paused: false }),
                AirflowDashboardHandler.listDags(Env, { limit: 1, onlyActive: true, paused: true }),
            ]);
            setFilterCounts({ all: all.total_entries, active: active.total_entries, paused: paused.total_entries });
        } catch {
            setFilterCounts(EMPTY_FILTER_COUNTS);
        }
    }, [Env, isAirflowConfigReady]);

    useEffect(() => {
        fetchFilterCounts();
    }, [fetchFilterCounts]);

    /*
     * Re-fetches the current page without the full-page loading spinner
     */
    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        fetchDags(false);
        fetchFilterCounts();
    }, [fetchDags, fetchFilterCounts]);

    /*
     * Sets the paused-state filter and resets to the first page
     */
    const handlePausedFilterChange = useCallback((value: PausedFilter) => {
        setPausedFilter(value);
        setOffset(0);
    }, []);

    /*
     * Sets the tag filter and resets to the first page
     */
    const handleTagFilterChange = useCallback((value: string) => {
        setTagFilter(value);
        setOffset(0);
    }, []);

    /*
     * Toggles a DAG's paused state optimistically, then confirms against the response
     */
    const handleTogglePause = useCallback(
        async (dag: AirflowDag) => {
            setTogglingDagId(dag.dag_id);
            try {
                await AirflowDashboardHandler.patchDag(Env, dag.dag_id, { is_paused: !dag.is_paused });
                setDags((prev) => prev.map((item) => (item.dag_id === dag.dag_id ? { ...item, is_paused: !dag.is_paused } : item)));
                successAlert(`DAG "${dag.dag_id}" ${dag.is_paused ? 'activated' : 'paused'}.`);
                fetchFilterCounts();
            } catch {
                errorAlert(`Failed to update DAG "${dag.dag_id}".`);
            } finally {
                setTogglingDagId(null);
            }
        },
        [Env, fetchFilterCounts]
    );

    /*
     * Opens the trigger dialog for a DAG
     */
    const handleTriggerRequest = useCallback((dagId: string) => {
        setTriggerDialogDagId(dagId);
    }, []);

    /*
     * Closes the trigger dialog and refreshes the list to reflect the new run
     */
    const handleTriggerDialogClose = useCallback(() => {
        setTriggerDialogDagId(null);
        fetchDags(false);
    }, [fetchDags]);

    /*
     * Blocks deletion of PI-integrator-managed DAGs, otherwise confirms and deletes
     */
    const handleDeleteRequest = useCallback(
        (dagId: string) => {
            if (dagId.startsWith(MODEL_REQUEST_DAG_PREFIX)) {
                errorAlert('This DAG is associated with PI-integrator, so deletion is not allowed.');
                return;
            }
            Modal.confirm({
                className: 'airflowDashboardRoot',
                title: 'Delete DAG?',
                content: `Are you sure you want to delete DAG "${dagId}"?`,
                okText: 'Yes, Delete',
                cancelText: 'Cancel',
                okButtonProps: { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } },
                onOk: async () => {
                    try {
                        await AirflowDashboardHandler.deleteDag(Env, dagId);
                        successAlert(dagDeletedToastMessage(dagId));
                        setDags((prev) => prev.filter((item) => item.dag_id !== dagId));
                        setTotalEntries((prev) => Math.max(0, prev - 1));
                        fetchFilterCounts();
                    } catch {
                        errorAlert('Error deleting DAG');
                    }
                },
            });
        },
        [Env, fetchFilterCounts]
    );

    /*
     * Navigates to a DAG's detail screen
     */
    const handleRowClick = useCallback(
        (dag: AirflowDag) => {
            history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dag.dag_id)}`);
        },
        [history]
    );

    const currentPage = useMemo(() => Math.floor(offset / DAG_LIST_LIMIT) + 1, [offset]);

    /*
     * Sets the current page, translating it back to an offset
     */
    const handlePageChange = useCallback((page: number) => {
        setOffset((page - 1) * DAG_LIST_LIMIT);
    }, []);

    return {
        dags,
        totalEntries,
        isLoading,
        isRefreshing,
        searchInput,
        pausedFilter,
        filterCounts,
        tagFilter,
        availableTags,
        sorting,
        togglingDagId,
        triggerDialogDagId,
        currentPage,
        handleSearchChange,
        handleRefresh,
        handlePausedFilterChange,
        handleTagFilterChange,
        setSorting,
        handleTogglePause,
        handleTriggerRequest,
        handleTriggerDialogClose,
        handleDeleteRequest,
        handleRowClick,
        handlePageChange,
    };
}
